/**
 * @author Nihar <nihar.baishya@314ecorp.com>
 * @description React Hook useCustomAttributesStore
 */
import _ from 'lodash';
import log from 'loglevel';
import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

import api from 'client/index';
import useAppStore from 'store/app';

const initialState: CustomAttributes.IState = {
	customAttributes: {} as Record<CustomAttributes.IEntityType, CustomAttributes.IAllResponse[]>,
	enabled: false,
};

const useCustomAttributesStore = create<CustomAttributes.IStore>()(
	devtools(
		(set) => ({
			...initialState,
			actions: {
				setCustomAttributes: (customAttributes) => set({ customAttributes }),
				setEnabled: (enabled) => set({ enabled }),
			},
		}),
		{ name: 'Custom Attributes Store' },
	),
);

// Store hooks to return actions and values
const useCustomAttributesStoreValues = (): CustomAttributes.IState =>
	useCustomAttributesStore(useShallow((state) => _.omit(state, ['actions'])));

const useCustomAttributesStoreActions = (): CustomAttributes.IActions => useCustomAttributesStore(useShallow((state) => state.actions));

/**
 * Fetch custom attributes from the API
 */
const fetchCustomAttributes = async (): Promise<void> => {
	const { userAccess } = useAppStore.getState();

	if (userAccess?.canAccessCustomAttributes) {
		const { actions } = useCustomAttributesStore.getState();
		actions.setEnabled(true);
		try {
			const { data } = await api.CustomAttributesApi.getEntityCustomAttributes();
			const sortedData = _.sortBy(data, (item) => _.toLower(item?.attrDef?.jsonschema?.title || ''));
			actions.setCustomAttributes(_.groupBy(sortedData, (item) => item.entityType) as AnyType);
		} catch (e: AnyType) {
			log.error(e);
		}
	}
	return null;
};

export { fetchCustomAttributes, useCustomAttributesStoreValues, useCustomAttributesStoreActions };
export default useCustomAttributesStore;
